/**
 * QuickStart information for Weave product.
 */

import {Button} from '@wandb/weave/components';
import {IconChevronNext} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';

import weavePlaygroundQuickstart from '../../../assets/screenshots/weave_playground_quickstart_2.png';
import {
  useProjectFromNameQuery,
  useUpsertModelMutation,
} from '../../../generated/graphql';
import * as Track from '../../../services/analytics';
import {QuickStartAnalyticsOrigin} from '../../../services/analytics/quickStartEvents';
import {doNotRetryContext, propagateErrorsContext} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';

type QuickStartWeaveProps = {
  entityName: string;
  projectName: string;
  analyticsOrigin: QuickStartAnalyticsOrigin;
};

// TODO: ask product if we can change this to a more descriptive name
const QUICKSTART_PROJECT_NAME = 'quickstart_playground';

export const QuickStartWeavePlayground = ({
  entityName,
  projectName,
  analyticsOrigin,
}: QuickStartWeaveProps) => {
  const targetProject =
    projectName && projectName !== '' ? projectName : QUICKSTART_PROJECT_NAME;
  const [upsertProjectMutation] = useUpsertModelMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
  });
  const playgroundProjectQuery = useProjectFromNameQuery({
    variables: {
      projectName: targetProject,
      entityName,
    },
  });

  const projectExists = useMemo(() => {
    if (playgroundProjectQuery.loading) {
      return {loading: true, projectExists: undefined};
    } else if (playgroundProjectQuery.data?.project?.id) {
      return {loading: false, projectExists: true};
    } else {
      return {loading: false, projectExists: false};
    }
  }, [playgroundProjectQuery.data, playgroundProjectQuery.loading]);

  const history = useHistory();
  return (
    <>
      <Tailwind className="h-screen">
        <div className="my-20 rounded-lg bg-moon-150 p-8">
          <img src={weavePlaygroundQuickstart}></img>
        </div>
        <div className="h-screen">
          <div className="flex w-[800px] content-start justify-between">
            <div className="my-6 flex-initial">
              <Button
                disabled={projectExists.loading}
                size="medium"
                onClick={async () => {
                  try {
                    Track.clickedWeavePlayground({
                      projectName: targetProject,
                      entityName,
                      location: analyticsOrigin,
                    });
                    if (!projectExists.projectExists) {
                      await upsertProjectMutation({
                        variables: {
                          description: QUICKSTART_PROJECT_NAME,
                          access: AccessOption.Private,
                          name: targetProject,
                          entityName,
                        },
                      });
                    }
                    history.push(
                      `/${entityName}/${targetProject}/weave/playground`
                    );
                  } catch (e) {
                    toast.error(
                      'Failed to create project: Please contact team admin to create a project.'
                    );
                    console.error(e);
                  }
                }}>
                <>
                  Start Exploring <IconChevronNext />
                </>
              </Button>
            </div>
          </div>
        </div>
      </Tailwind>
    </>
  );
};
